import { canCreateItem, canExportSequenceOnClient, canUpdateItem, canUploadItemOnClient } from '@mimir/common/util/security';
import { invariant } from '@mimir/common/util/invariant';
import type { CancelTokenManager} from '@mimir/client-common/util/subclip';
import { prepareSubclipBrowserBlob } from '@mimir/client-common/util/subclip';
import type { VideoApiActions } from '@mimir/client-common/util/apiActions';
import { SegmentUrlCache } from '@mimir/client-common/util/segmentUrlCache';
import type { ProgressFunc } from '@mimir/common/format/mp4Subclip';
import noop from 'lodash/noop';
import EventBus, { EVENTS } from '@mimir/web/eventBus';
import type { SearchResultHit, Item, LoadedItem } from '@mimir/common/dto/searchResultHit';
import { ItemType } from '@mimir/common/dto/itemTypes';
import { AsyncOperationState } from '@mimir/common/dto/asyncOperationPrimitives';
import type { AsyncOperationType } from '@mimir/common/dto/asyncOperationType';
import cloneDeep from 'lodash/cloneDeep';
import keyBy from 'lodash/keyBy';
import remove from 'lodash/remove';
import type { ErrorMessageFieldName } from '@mimir/common/search/interfaces';
import { DETECTED_TEXT_MDF_ID, SystemDefaultField } from '@mimir/common/mdf/mdfDefaults';
import {
  getItemSystemDefaultField,
  DetectedTextField,
} from '@mimir/common/mdf/MetaDataForm';
import type { Filter } from '@mimir/common/filters/Filters';
import { isInstanceOfPersonDto } from '@mimir/common/dto/personModel';
import { isSuperAdmin } from './security';
import type { IProgressNotification, NotificationStackActionBtn } from '@mimir/web/model/notificationTypes';
import { NotificationStackItemType } from '@mimir/web/model/notificationTypes';
import { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import { getGlobalRouter } from '@mimir/web/router';
import type { TimedMetadataItemModel } from '@mimir/web/store/timed-mdf-module-state';
import type { StyleValue } from 'vue/types/jsx';
import { addNotification, deleteNotification, updateNotification } from '../globals/notifications';
import { SafeHtmlBuilder } from '@mimir/common/util/safeHtmlBuilder';
import type { FolderDto } from '@mimir/common/dto/folderModels';
import { sendApiCommand } from '@mimir/common/client/sendApiCommand';
import { globalBackendClient } from '@mimir/client-common/globals/backendClient';
import { GetEXIFEmbeddedImageURLCommand } from '@mimir/common/api/mdfToEXIFEmbeddedImageURLCommands';

export const downloadableVideStreamRoles = <const> [StreamRoleId.LiveRecordHighresTs, StreamRoleId.LiveRecordWebproxyTs];
export type DownloadableVideStreamRoleType = typeof downloadableVideStreamRoles[number];

export class DownloadLiveMediaNotifier {
  private readonly notification: IProgressNotification;
  private itemTitle: string;
  private downloadType: string;

  constructor(
    itemId: string,
    itemTitle: string,
    streamRoleId: DownloadableVideStreamRoleType,
    routeBtn: Omit<NotificationStackActionBtn, 'icon'>,
    cancelBtn: Omit<NotificationStackActionBtn, 'icon'>,
  ) {
    const downloadType = streamRoleId === StreamRoleId.LiveRecordHighresTs ? 'highres' : 'proxy';

    this.notification = {
      routeBtn,
      cancelBtn,
      type: NotificationStackItemType.Progress,
      key: `${itemId}-${streamRoleId}`,
      message: new SafeHtmlBuilder().addText(`Downloading ${downloadType} of `).addBoldText(itemTitle),
      progress: 0,
      isClosed: false,
      isComplete: false,
    };
    addNotification(this.notification);

    this.itemTitle = itemTitle;
    this.downloadType = downloadType;
  }

  public progress(percent: number) {
    this.notification.progress = percent;
    updateNotification(this.notification);
  }

  public completed(actionFn: () => void) {
    this.notification.message = new SafeHtmlBuilder().addText(`Downloaded ${this.downloadType} of `).addBoldText(this.itemTitle);
    this.notification.actionBtn = {
      icon: 'download',
      tooltip: 'Save',
      handler: actionFn,
    };
    updateNotification(this.notification);
  }

  public dismiss() {
    this.notification.isClosed = true;
    updateNotification(this.notification);
    deleteNotification(this.notification);
  }
}

export const getDownloadLiveMediaNotifier = (
  itemId: string,
  itemTitle: string,
  streamRoleId: DownloadableVideStreamRoleType,
  ctm: CancelTokenManager,
) => {
  const notifier = new DownloadLiveMediaNotifier(
    itemId,
    itemTitle,
    streamRoleId,
    {
      tooltip: 'Open item',
      handler: () => getGlobalRouter().push({
        name: 'itemDetails',
        params: { id: itemId },
      }).catch(noop),
    },
    {
      tooltip: 'Cancel download',
      handler: () => {
        notifier.dismiss();
        ctm.cancelAll();
      },
    },
  );

  return notifier;
};

export const getCloudLiveMediaBlob = async (
  item: Pick<Item, 'id'| 'media'>,
  streamRoleId: DownloadableVideStreamRoleType,
  progressFn: ProgressFunc,
  api: VideoApiActions,
  ctm: CancelTokenManager,
) => {
  invariant(item.media, 'Expected to be live item');

  const highresBlob = await prepareSubclipBrowserBlob(
    api,
    new SegmentUrlCache(api, 'get', [streamRoleId]),
    item.id,
    item.media.segmentDuration,
    0,
    // Note that this assumes that we will not be using this function
    // to download fragmented Mp4 proxies. This is currently the case
    // since streamRoleId is limited to MpegTs variants.
    item.media.segmentCount - 1,
    false,
    progressFn,
    streamRoleId,
    ctm,
  );

  if (!highresBlob) {
    const blobType = streamRoleId === StreamRoleId.LiveRecordHighresTs ? 'highres' : 'webProxy';
    console.error(`Couldn't generate ${blobType} blob.`);
    throw new Error('Couldn\'t generate blob');
  }

  return highresBlob;
};

export const canUploadToPlaceholder = (permissions: string[]) => {
  return doesHaveUploadPermission(permissions) && canUpdateItem(permissions);
};

export const disabledUploadMessageOrTooltip = (permissions: string[], isUpdatableByUser: boolean) => {
  if (!doesHaveUploadPermission(permissions)) return 'You do not have permission to upload';
  if (!canUpdateItem(permissions)) return 'You do not have permission to update existing items';
  if (!isUpdatableByUser) return 'You don\'t have permission to update this item';
  return undefined;
};

export const doesHaveUploadPermission = (permissions: string[]) => {
  return canCreateItem(permissions) && canUploadItemOnClient(permissions);
};

export const doesHaveExportSequencePermission = (permissions: string[]) => {
  return canCreateItem(permissions) && canExportSequenceOnClient(permissions);
};

/**
 * Prepares a string representation from number of seconds
 * @param d{Number of seconds to convert}
 */
export const secondsToHms = (seconds: number) => {
  const normalizedSeconds = Number(seconds);
  if (Number.isNaN(normalizedSeconds)) return '';

  const h = Math.floor(normalizedSeconds / 3600);
  const m = Math.floor(normalizedSeconds % 3600 / 60);
  const s = Math.floor(normalizedSeconds % 3600 % 60);

  const hDisplay = h > 0 ? h + (h == 1 ? ' hour, ' : ' hours, ') : '';
  const mDisplay = m > 0 ? m + (m == 1 ? ' minute, ' : ' minutes, ') : '';
  const sDisplay = s > 0 ? s + (s == 1 ? ' second' : ' seconds') : '';
  return hDisplay + mDisplay + sDisplay;
};

export const getDisplayTitle = (item: FolderDto | Pick<Item, 'name' | 'metadata' | 'itemType'>, highlighted = false) => {
  // @ts-ignore
  if (item.itemType === ItemType.FOLDER) {
    return item.name;
  }
  return getItemSystemDefaultField(item, SystemDefaultField.Title, highlighted);
};

export const getErrorMessageFieldName = (operationType: AsyncOperationType): ErrorMessageFieldName | null => {
  switch (operationType) {
    case 'transcoding': return  'transcodingErrorMessage';
    case 'transcription': return 'transcriptionErrorMessage';
    case 'personDetection': return 'personDetectionErrorMessage';
    case 'celebrityDetection': return 'celebrityDetectionErrorMessage';
    case 'labelDetection': return 'labelDetectionErrorMessage';
    case 'textDetection': return 'textDetectionErrorMessage';
    case 'archiveFileTransfer': return 'archiveFileTransferErrorMessage';
    default: return null;
  }
};

export const getStateFromItem = (item: LoadedItem, operationType: AsyncOperationType) => {
  switch(operationType) {
    case 'transcoding': return item.transcodingState;
    case 'transcription': return item.transcriptionState;
    case 'personDetection': return item.personDetectionState;
    case 'celebrityDetection': return item.celebrityDetectionState;
    case 'labelDetection': return item.labelDetectionState;
    case 'textDetection': return item.textDetectionState;
    case 'archiveFileTransfer': return item.archiveFileTransferState;
    default: return null;
  }
};

export const getErrorMessageFromItem = (item: LoadedItem, operationType: AsyncOperationType) => {
  switch(operationType) {
    case 'transcoding': return item.transcodingErrorMessage;
    case 'transcription': return item.transcriptionErrorMessage;
    case 'personDetection': return item.personDetectionErrorMessage;
    case 'celebrityDetection': return item.celebrityDetectionErrorMessage;
    case 'labelDetection': return item.labelDetectionErrorMessage;
    case 'textDetection': return item.textDetectionErrorMessage;
    case 'archiveFileTransfer': return item.archiveFileTransferErrorMessage;
    default: return null;
  }
};

export const isProcessDisabled = (item: LoadedItem, type: AsyncOperationType) => {
  switch (type) {
    case 'personDetection':
      return (item.personDetectionEnabled !== undefined && !item.personDetectionEnabled) ||
        item.personDetectionState === AsyncOperationState.PROGRESSING;
    case 'transcription':
      return (item.transcriptionEnabled !== undefined && !item.transcriptionEnabled) ||
        item.transcriptionState === AsyncOperationState.PROGRESSING;
    case 'celebrityDetection':
      return (item.celebrityDetectionEnabled !== undefined && !item.celebrityDetectionEnabled) ||
        item.celebrityDetectionState === AsyncOperationState.PROGRESSING;
    case 'labelDetection':
      return (item.labelDetectionEnabled !== undefined && !item.labelDetectionEnabled) ||
        item.labelDetectionState === AsyncOperationState.PROGRESSING;
    case 'textDetection':
      return (item.textDetectionEnabled !== undefined && !item.textDetectionEnabled) ||
        item.textDetectionState === AsyncOperationState.PROGRESSING;
    default: return false;
  }
};

export const reflect = <T>(
  p: Promise<T>,
): Promise<{ val: T | Error; status: 'resolved' | 'rejected' }> =>
    p.then(
    (val: T) => ({ val, status: 'resolved' }),
    (err: Error) => ({ val: err, status: 'rejected' }),
    );

export interface NonMedia {
  text: string;
  color: string;
}

/**
 * Updates range values of a date filter.
 * @param dateArray Array of date strings
 * @param dateFilter the filter to update
 * @param isDateSortProperty Whether this filter is also the sort property
 */
export const updateDateFiltersDateRange = (dateArray: Date[], dateFilter: Filter<Date>, isDateSortProperty: boolean) => {
  if (dateArray?.length > 0) {
    if (isDateSortProperty) {
      EventBus.emit(EVENTS.RESET_DATEFILTER);
    }
    dateFilter.setValue(dateArray);
  }
};

/**
 * Chrome 65 removed support for the download attribute on anchor elements with cross-origin.
 *
 * This function fetches a file from a specified download URL, converts it to a blob, and enables downloading with an optional filename.
 * Allows downloading a file with a different name without requiring server-side response-content-disposition header.
 *
 * @param downloadUrl Download url for the file. This is typically a s3 signed url.
 * @param downloadAs Optional. Specifies the name under which the downloaded file will be saved.
 */
export const convertFileToBlobAndDownload = async (downloadUrl: string, downloadAs?: string) => {
  const blob = await fetch(downloadUrl).then(response => response.blob());
  const url = URL.createObjectURL(blob);
  const anchorElement = document.createElement('a');
  anchorElement.style.display = 'none';
  anchorElement.href = url;

  if (downloadAs) anchorElement.download = downloadAs;

  document.body.appendChild(anchorElement);
  anchorElement.click();
  window.URL.revokeObjectURL(url);
};

interface GetEXIFEmbeddedImageURLOutput {
  message: string;
  downloadUrl: string;
  hasExifError?: boolean;
}
export const getEXIFEmbeddedImageURL = async (
  url: string,
  fileName: string,
  itemId: string
): Promise<GetEXIFEmbeddedImageURLOutput | null> => {

  const payload = {
    url,
    fileName,
    itemId
  };

  try {
    const response = await sendApiCommand(
      globalBackendClient,
      new GetEXIFEmbeddedImageURLCommand(payload)
    );

    if (response) {
      return response;
    } else {
      console.error('No signed URL in response');
      return null;
    }
  } catch (error) {
    console.error('Failed to fetch EXIF embedded image URL:', error);
    return null;
  }
};

function iframeDownload(url: string, filename?: string) {
  // The problem with downloading multiple items using anchor element's click method
  // is that if there are downloads queued (e.g., due to chrome's max 6 tcp connections
  // to the same origin at a time as s3 servers are http 1.1) and click is being called
  // for multiple items the previous one gets canceled by the next one and so on. So we
  // use iframes to work around that limitation.
  const iframe = document.createElement('iframe');
  iframe.setAttribute('style', 'display: none');
  iframe.setAttribute('sandbox', 'allow-downloads allow-scripts');
  document.body.appendChild(iframe);
  iframe.src = url;
  // If the iframe couldn't even start download due to queueing as above for the timeout
  // (1 hour) used below, the download will be canceled but this is a more reliable strategy
  // than using anchor elements as described above which cancels immediately when the next
  // click is invoked. Let's remove the iframes so that they don't stay in memory.
  setTimeout(() => {
    document.body.removeChild(iframe);
  }, 60 * 60 * 1000);

  return Promise.resolve();
}

function downloadBlob(blob: Blob, filename?: string) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  filename && anchor.setAttribute('download', filename);
  anchor.setAttribute('href', url);
  anchor.style.display = 'none';

  anchor.addEventListener('click', function handler(e) {
    e.stopPropagation();
    this.removeEventListener('click', handler);
  });

  document.body.appendChild(anchor);

  return new Promise<void>((resolve) => {
    setTimeout(() => {
      anchor.click();
      document.body.removeChild(anchor);

      setTimeout(() => {
        window.URL.revokeObjectURL(anchor.href);
      }, 250);

      resolve();
    }, 100);
  });
}

export const download = (data: string | ArrayBuffer | Blob, filename?: string) => {
  if (typeof data === 'string') {
    return iframeDownload(data, filename);
  }

  if (data instanceof ArrayBuffer) {
    const blob = new Blob([data], { type: 'text/plain;charset=utf-8', });

    const maybeIe10: { msSaveBlob?: (blob: Blob, filename: string | undefined) => void } & Navigator = navigator;
    // IE10+: (has Blob, but not a[download] or URL)
    if (maybeIe10.msSaveBlob) {
      maybeIe10.msSaveBlob(blob, filename);
      return Promise.resolve();
    }

    return downloadBlob(blob, filename);
  }

  return downloadBlob(data, filename);
};



export const filterOutMatchingPersonImages = (items: SearchResultHit[]) : Record<string, SearchResultHit> | null => {
  if (items && items.length > 0) {
    const filteredItems = cloneDeep(items);
    for (const item of items) {
      if (isInstanceOfPersonDto(item) && item.imageIds && item.imageIds.length > 0) {
        for (const imageId of item.imageIds) {
          remove(filteredItems, (i) => i.id === imageId);
        }
      }
    }
    return keyBy(filteredItems, i => i.id);
  }
  return null;
};

export const getDataUrl = (sourceHtmlElement: HTMLImageElement, count: number) => {
  if (!sourceHtmlElement || sourceHtmlElement.width <= 0 || sourceHtmlElement.height <= 0) {
    return;
  }

  const canvas = document.createElement('canvas');
  const sourceWidth = sourceHtmlElement.width as number;
  const sourceHeight = sourceHtmlElement.height as number;

  // height 60, to match with in-browser drag thumbnail size as specified in .drag-thumbnail
  // from clients/vuetify/src/pages/folders/components/DragContent.vue
  const destHeight = 60;

  // width calculated to preserve aspect ratio of source
  const destWidth = destHeight * (sourceWidth / sourceHeight);

  // canvas should have a border to match with the .drag-thumbnail class
  canvas.height = destHeight + 2;
  canvas.width = destWidth + 2;

  // draw the border
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = 'black';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.strokeStyle = '#FFFFFF';
  ctx.strokeRect(0, 0, canvas.width, canvas.height);

  // draw the image
  ctx.drawImage(sourceHtmlElement, 1, 1, destWidth, destHeight);

  if (count && count > 1) {
    ctx.beginPath();
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    ctx.arc(centerX, centerY, canvas.height / 4, 0, 2 * Math.PI);
    ctx.fillStyle = "black";
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = "white";
    ctx.stroke();

    ctx.fillStyle = "white";
    const text = `${count}`;
    const textMetrics = ctx.measureText(text);

    ctx.fillText(text, centerX - textMetrics.width / 2, centerY + textMetrics.actualBoundingBoxAscent / 2, canvas.width / 2);
  }

  // convert png and return
  const dataURL = canvas.toDataURL('image/png');
  return dataURL;
};

/**
 * Determines whether item's highres is in archive bucket by checking file transfer state
 * or archive bucket properties
 */
export const isHighresInArchiveBucket = (item: Pick<LoadedItem, 'archiveFileTransferState'> | undefined | null) => {
  return item?.archiveFileTransferState === AsyncOperationState.COMPLETE;
};

export const getProfilePictureVBadgeColor = () => {
  return isSuperAdmin() ? 'orange accent-4' : '#96E6B3';
};

export const getProfilePictureVBadgeIconColor = () => {
  return isSuperAdmin() ? '#fff' : '#28413B';
};

export const getArchivalStatusMessage = (
  item: Pick<LoadedItem, 'isArchived' | 'archiveFileTransferState'> | undefined | null,
  archiveTransferState?: AsyncOperationState, noActionInstruction = false,
  hasArchivePermission?: boolean,
) => {
  const fileTransferState = archiveTransferState ?? item?.archiveFileTransferState;

  if (!item?.isArchived && !fileTransferState) return '';

  if (item?.isArchived && !fileTransferState) {
    return 'Item is archived';
  }

  if (isHighresInArchiveBucket(item)) {
    return 'The highres of this item is in an archive bucket';
  }

  if (fileTransferState === AsyncOperationState.INITIATED) {
    return 'File transfer to archive bucket is pending';
  }

  if (fileTransferState === AsyncOperationState.PROGRESSING) {
    return 'File transfer to archive bucket is in progress';
  }

  if (fileTransferState === AsyncOperationState.ERROR) {
    const retryMsg = hasArchivePermission ? ' Click to retry.' : ' Need permission to retry.';
    return `Failed to transfer file to archive bucket.${noActionInstruction ? '' : retryMsg}`;
  }

  return '';
};

export function canItemProduceFrames(item: Pick<LoadedItem, 'itemType' | 'media'> | null | undefined): boolean {
  return item?.itemType === ItemType.VIDEO
    && (!item.media || item.media.segmentCount > 0);
}


export const getDetectedTextBoundingBoxStyle = (
  timedMetadata: TimedMetadataItemModel,
  playerInfo?: {
    currentTimeInMs: number,
    videoAspectRatio: number | null,
    playerAspectRatio: number | null,
  },
) => {
  const bbStyle: StyleValue = {
    position: 'absolute',
    border: '1px solid red',
    zIndex: '5',
    display: 'none',
  };

  if (!timedMetadata || timedMetadata.data.formId !== DETECTED_TEXT_MDF_ID) {
    return bbStyle;
  }

  const startMs = timedMetadata.startMs;
  const endMs = timedMetadata.endMs;
  if (playerInfo) {
    const currentTime = playerInfo.currentTimeInMs;
    if (currentTime < startMs || currentTime > endMs) {
      return bbStyle;
    }
  }

  const formData = timedMetadata.data.formData;

  // player width and height in percentage
  const pWidth = 100;
  const pHeight = 100;

  let top = pHeight * (formData[DetectedTextField.Top] as number);
  let left = pWidth * (formData[DetectedTextField.Left] as number);
  let width = pWidth * (formData[DetectedTextField.Width] as number);
  let height = pHeight * (formData[DetectedTextField.Height] as number);

  if (playerInfo && playerInfo.videoAspectRatio !== null && playerInfo.playerAspectRatio !== null) {
    /**
     * The bounding box position is relative to the actual video width and height.
     * This aspect ratio can be different from the wrapper div surrounding the player, that
     * also hosts the bounding box div that we draw as overlay. So we need to adjust
     * the bounding box coodinates according to how wider or narrower the wrapper is
     * compared to the actual video.
     */
    const widthFactor = playerInfo.playerAspectRatio / playerInfo.videoAspectRatio;
    if (widthFactor > 1) {
      const leftFromMid = left - 50;
      width /= widthFactor;
      left = 50 + (leftFromMid / widthFactor);
    } else {
      const topFromMid = top - 50;
      height *= widthFactor;
      top = 50 + (topFromMid * widthFactor);
    }
  }

  bbStyle.top = `${top}%`;
  bbStyle.left = `${left}%`;
  bbStyle.width = `${width}%`;
  bbStyle.height = `${height}%`;
  bbStyle.display = 'block';

  return bbStyle;
};

